import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  useTheme,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import { OrderProduct } from "../../../../../models/Order/OrderProduct";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CloseIcon from "@mui/icons-material/Close";
import { observer } from "mobx-react";

interface RaiseStockCheckReqPopupProps {
  data: OrderProduct[];
  open: boolean;
  onClose: () => void;
  onSave: (
    products: { product_id: number; notes: string; name: string }[],
  ) => void;
  /** Unpaid sample orders: user selects which products to include in SCR. */
  allowProductSelection?: boolean;
}

const RaiseStockCheckReqPopup = ({
  data,
  open,
  onClose,
  onSave,
  allowProductSelection = false,
}: RaiseStockCheckReqPopupProps) => {
  const theme = useTheme();

  const [productNotes, setProductNotes] = useState<
    { product_id: number; notes: string; error: boolean; name: string }[]
  >([]);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(
    new Set(),
  );

  useEffect(() => {
    setProductNotes(
      data.map((product) => ({
        product_id: product.id,
        name: product.name!,
        notes: "",
        error: false,
      })),
    );
    setSelectedProductIds(new Set(data.map((product) => product.id)));
  }, [data]);

  const handleInputChange = (id: number, value: string) => {
    setProductNotes((prevNotes) =>
      prevNotes.map((note) =>
        note.product_id === id ? { ...note, notes: value, error: false } : note,
      ),
    );
  };

  const toggleProductSelection = (productId: number) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleSave = () => {
    const notesToValidate = allowProductSelection
      ? productNotes.filter((note) => selectedProductIds.has(note.product_id))
      : productNotes;

    if (allowProductSelection && notesToValidate.length === 0) {
      return;
    }

    let hasError = false;

    const updatedNotes = productNotes.map((note) => {
      const isSelected = allowProductSelection
        ? selectedProductIds.has(note.product_id)
        : true;

      if (!isSelected) {
        return { ...note, error: false };
      }

      if (!note.notes.trim()) {
        hasError = true;
        return { ...note, error: true };
      }
      return note;
    });

    setProductNotes(updatedNotes);

    if (!hasError) {
      onSave(
        notesToValidate.map(({ product_id, notes, name }) => ({
          product_id,
          notes,
          name,
        })),
      );
      handleClearAll();
      onClose();
    }
  };

  const handleClearAll = () => {
    setProductNotes((prevNotes) =>
      prevNotes.map((note) => ({ ...note, notes: "", error: false })),
    );
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 4,
          pb: 2,
          pt: 2,
        }}
      >
        <CustomTypography
          sx={{ pb: "0px" }}
          style={{ paddingBottom: "0px" }}
          variant="heading4"
        >
          Raise Stock Check Request
        </CustomTypography>
        <IconButton
          sx={{ left: "12px" }}
          aria-label="close"
          onClick={() => {
            handleClearAll();
            onClose();
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 4, pb: 1 }}>
        {allowProductSelection ? (
          <CustomTypography
            variant="body2"
            sx={{ mb: 2, color: theme.palette.customColors.greyTextColor }}
          >
            Select the products going via the PR/PO flow. Products on a pending
            stock issue request are not listed here.
          </CustomTypography>
        ) : null}
        {productNotes?.map((product) => (
          <Box key={product.product_id}>
            {allowProductSelection ? (
              <FormControlLabel
                sx={{ mb: 0.5 }}
                control={
                  <Checkbox
                    checked={selectedProductIds.has(product.product_id)}
                    onChange={() => toggleProductSelection(product.product_id)}
                  />
                }
                label={
                  <CustomTypography variant="body1" fontWeight={600}>
                    {product.name}
                  </CustomTypography>
                }
              />
            ) : (
              <CustomTypography
                variant="body1"
                fontWeight={500}
                style={{
                  color: theme.palette.customColors.greyTextColor,
                }}
              >
                Product {product.name}
              </CustomTypography>
            )}
            {(!allowProductSelection ||
              selectedProductIds.has(product.product_id)) && (
              <FormTextField
                label="Write Product Notes"
                required={true}
                multiline
                sx={{ mt: 1, mb: 2 }}
                rows={3}
                value={product.notes}
                onChange={(id, e) => handleInputChange(product.product_id, e!)}
                error={product.error}
              />
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions
        sx={{ p: 4, pt: 0, display: "flex", justifyContent: "space-between" }}
      >
        <AppButton
          onClick={handleClearAll}
          label="Clear All"
          variant="grayscale"
        />

        <AppButton onClick={handleSave} label="Confirm" variant="primary" />
      </DialogActions>
    </Dialog>
  );
};

export default observer(RaiseStockCheckReqPopup);
